import React from "react";
import { Info, AlertTriangle, AlertCircle, CheckCircle2, X } from "lucide-react";

export type SeparationAlertType = "info" | "warning" | "error" | "success";

export interface SeparationAlertProps {
  type?: SeparationAlertType;
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode | boolean;
  className?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  onClose?: () => void;
}

const TYPE_CONFIG: Record<
  SeparationAlertType,
  {
    containerClass: string;
    titleClass: string;
    descClass: string;
    iconClass: string;
    defaultIcon: React.ReactNode;
  }
> = {
  info: {
    containerClass:
      "bg-blue-50/60 border border-blue-200/80 border-l-[3.5px] border-l-blue-500 text-slate-800",
    titleClass: "text-blue-950 font-semibold",
    descClass: "text-slate-600",
    iconClass: "text-blue-600",
    defaultIcon: <Info className="w-4 h-4 shrink-0 text-blue-600 mt-0.5" />,
  },
  warning: {
    containerClass:
      "bg-amber-50/80 border border-amber-200/90 border-l-[3.5px] border-l-amber-500 text-amber-950",
    titleClass: "text-amber-900 font-semibold",
    descClass: "text-amber-800/90",
    iconClass: "text-amber-600",
    defaultIcon: (
      <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
    ),
  },
  error: {
    containerClass:
      "bg-rose-50/80 border border-rose-200/90 border-l-[3.5px] border-l-rose-500 text-rose-950",
    titleClass: "text-rose-900 font-semibold",
    descClass: "text-rose-800/90",
    iconClass: "text-rose-600",
    defaultIcon: (
      <AlertCircle className="w-4 h-4 shrink-0 text-rose-600 mt-0.5" />
    ),
  },
  success: {
    containerClass:
      "bg-emerald-50/80 border border-emerald-200/90 border-l-[3.5px] border-l-emerald-500 text-emerald-950",
    titleClass: "text-emerald-900 font-semibold",
    descClass: "text-emerald-800/90",
    iconClass: "text-emerald-600",
    defaultIcon: (
      <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" />
    ),
  },
};

export const SeparationAlert: React.FC<SeparationAlertProps> = ({
  type = "info",
  title,
  description,
  icon,
  className = "",
  children,
  action,
  onClose,
}) => {
  const config = TYPE_CONFIG[type] ?? TYPE_CONFIG.info;

  const renderIcon = () => {
    if (icon === false) return null;
    if (icon !== undefined && icon !== true) return icon;
    return config.defaultIcon;
  };

  const renderedIcon = renderIcon();

  return (
    <div
      role="alert"
      className={`rounded-r-lg rounded-l-sm p-3 sm:p-3.5 shadow-sm transition-all ${config.containerClass} ${className}`}
    >
      <div className="flex items-start gap-2.5">
        {renderedIcon && <div className="shrink-0">{renderedIcon}</div>}
        <div className="flex-1 min-w-0 text-sm">
          {title && <div className={`${config.titleClass}`}>{title}</div>}
          {description && (
            <div
              className={`text-xs sm:text-sm ${title ? "mt-0.5" : ""} ${config.descClass}`}
            >
              {description}
            </div>
          )}
          {children && (
            <div
              className={`text-xs sm:text-sm ${title || description ? "mt-1.5" : ""} ${config.descClass}`}
            >
              {children}
            </div>
          )}
        </div>
        {action && <div className="shrink-0 self-center">{action}</div>}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SeparationAlert;
